import {
  DynamicFormVariant,
  FORM_FIELD_TYPES,
  type FormDefinition,
  type FormField,
  type FormSection,
  type FormStep,
} from '~/constants/formTypes'
import * as Yup from 'yup'
import { usePositionStore } from '~/stores/positionStore'
import { useOrgStore } from '~/stores/orgStore'
import { computed, defineAsyncComponent } from 'vue'
import { useGlobalModal } from '~/composables/useGlobalModal'
import { TeamSize } from '~/constants/orgTypes'
import CheckerPanel from '~/components/intake/CheckerPanel.vue'
import { publicOrganizationName } from '~/utils/publicOrganizationName'

const companyNameIntake = 'companyName'
const companyNameIntakeField: FormField = {
  label: 'What is the company name?',
  name: companyNameIntake,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const orgStore = useOrgStore()
    return publicOrganizationName(orgStore.selected?.companyName)
  }),
  onUpdate: (value: any) => {
    const orgStore = useOrgStore()
    orgStore.updateOrgField('companyName', value)
  },
  componentProps: {
    placeholder: 'Enter company name',
  },
}

const companyWebsiteIntake = 'companyWebsite'
const companyWebsiteIntakeField: FormField = {
  label: "What is the company's website?",
  name: companyWebsiteIntake,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const orgStore = useOrgStore()
    return orgStore.selected?.companyWebsite || ''
  }),
  onUpdate: (value: any) => {
    const orgStore = useOrgStore()
    orgStore.updateOrgField('companyWebsite', value)
  },
  componentProps: {
    placeholder: 'Enter company website URL',
  },
}

const companyStage = 'companySize'
const companyStageField: FormField = {
  label: 'Which stage best describes the company right now?',
  name: companyStage,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const orgStore = useOrgStore()
    return orgStore.selected?.companySize || ''
  }),
  onUpdate: (value: any) => {
    const orgStore = useOrgStore()
    orgStore.updateOrgField('companySize', value)
  },
  componentProps: {
    placeholder: 'Example: Series A, Series B+...',
  },
}

const companyMission = 'missionVision'
const companyMissionField: FormField = {
  label: "Briefly describe what the company's mission is.",
  name: companyMission,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const orgStore = useOrgStore()
    return orgStore.selected?.missionVision || ''
  }),
  onUpdate: (value: any) => {
    const orgStore = useOrgStore()
    orgStore.updateOrgField('missionVision', value)
  },
  componentProps: {
    placeholder: 'Enter company description',
  },
}

const companyInfoIntakeSection: FormSection = {
  id: 'companyInfoIntakeSection',
  label: 'Company Information',
  description: "Let's start with a high-level overview. This helps us understand the big picture before we dive into the details. Please answer every question to move forward.",
  fields: [companyNameIntakeField, companyWebsiteIntakeField, companyStageField, companyMissionField],
}

const hiringTeam = 'hiringTeam'
const hiringTeamField: FormField = {
  label: 'Which team is making this hire?',
  name: hiringTeam,
  componentType: FORM_FIELD_TYPES.selectCustom,
  componentProps: {
    placeholder: 'Select a team',
    options: () => {
      const teamStore = useTeamStore()
      return teamStore.list?.map((team) => ({
        value: team.id,
        label: team.name,
      })) ?? []
    },
    ctaText: 'Add Team +',
    ctaClick: () => {
      const positionStore = usePositionStore()
      positionStore.updatePositionField('hiringTeam', 'addNewTeam')
    },
  },
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.hiringTeam || ''
  }),
  displayValue: computed(() => {
    const teamStore = useTeamStore()
    const positionStore = usePositionStore()
    if (positionStore.selected?.positionData?.hiringTeam) {
      const team = teamStore.getTeam(
        positionStore.selected?.positionData?.hiringTeam,
      )
      return team?.name ?? ''
    }
    return ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(hiringTeam, value)
  },
}

const newTeamName = 'newTeamName'
const newTeamNameField: FormField = {
  label: 'Team Name',
  name: newTeamName,
  required: true,
  subLabel: 'a',
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().when([], {
    is: () => {
      const positionStore = usePositionStore()
      return positionStore.selected?.positionData?.hiringTeam === 'addNewTeam'
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.newTeamName || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(newTeamName, value)
  },
  placeholder: 'Enter team name',
  indent: true,
  checkShouldShow: () => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.hiringTeam === 'addNewTeam'
  },
}

const newTeamDescription = 'newTeamDescription'
const newTeamDescriptionField: FormField = {
  label: 'Team Description',
  name: newTeamDescription,
  required: true,
  subLabel: 'b',
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().when([], {
    is: () => {
      const positionStore = usePositionStore()
      return positionStore.selected?.positionData?.hiringTeam === 'addNewTeam'
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.newTeamDescription || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(newTeamDescription, value)
  },
  placeholder: 'Enter team description',
  indent: true,
  checkShouldShow: () => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.hiringTeam === 'addNewTeam'
  },
}

const newTeamSize = 'newTeamSize'
const newTeamSizeField: FormField = {
  label: 'Team Size',
  name: newTeamSize,
  required: true,
  subLabel: 'c',
  componentType: FORM_FIELD_TYPES.selectCustom,
  componentProps: {
    placeholder: 'Select team size',
    options: () => {
      return Object.values(TeamSize).map((s) => ({ value: s, label: s }))
    },
  },
  rules: Yup.string().when([], {
    is: () => {
      const positionStore = usePositionStore()
      return positionStore.selected?.positionData?.hiringTeam === 'addNewTeam'
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.newTeamSize || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(newTeamSize, value)
  },
  checkShouldShow: () => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.hiringTeam === 'addNewTeam'
  },
  indent: true,
}

const teamFocus = 'teamFocus'
const teamFocusField: FormField = {
  candidateVisible: true,
  label: "What is the team's focus?",
  name: teamFocus,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.teamFocus || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(teamFocus, value)
  },
  fillValue: computed(() => {
    const teamStore = useTeamStore()
    const positionStore = usePositionStore()
    if (positionStore.selected?.positionData?.hiringTeam) {
      const team = teamStore.getTeam(
        positionStore.selected?.positionData?.hiringTeam,
      )
      return team?.intro ?? ''
    }
    return ''
  }),
  placeholder: 'This team builds and maintains the features throughout the site',
}

const companyTeamFields: FormField[] = [hiringTeamField, newTeamNameField, newTeamDescriptionField, newTeamSizeField, teamFocusField]

const companyTeamSection: FormSection = {
  id: 'companyTeamSection',
  label: 'About the team',
  fields: companyTeamFields,
}

const businessNeed = 'businessNeed'
const businessNeedField: FormField = {
  candidateVisible: true,
  label: 'What business need will this role solve for the company?',
  name: businessNeed,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.businessNeed || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(businessNeed, value)
  },
  placeholder: 'The role will solve...',
  helpText: 'Every role should solve a problem or unlock an opportunity. Stating the business need gives the role a clear purpose and helps us find someone who is motivated by that specific challenge.',
}

const roleType = 'roleType'
const roleTypeField: FormField = {
  candidateVisible: true,
  label: 'What type of role is it?',
  name: roleType,
  componentType: FORM_FIELD_TYPES.radio,
  componentProps: {
    options: [
      { value: 'newRole', label: 'New Role' },
      { value: 'temporaryBackfill', label: 'Temporary Backfill (Replacing an existing role temporarily)' },
      { value: 'permanentBackfill', label: 'Permanent Backfill (Replacing an existing role permanently)' },
    ],
  },
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.roleType || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(roleType, value)
  },
}

const urgency = 'urgency'
const urgencyField: FormField = {
  label: 'What is the urgency of filling this position?',
  name: urgency,
  componentType: FORM_FIELD_TYPES.radio,
  componentProps: {
    options: [
      { value: 'high', label: 'High Urgency (Extremely urgent, requires immediate attention)' },
      { value: 'medium', label: 'Medium Urgency (Important but not critical)' },
      { value: 'low', label: 'Low Urgency (Can be handled when time permits.)' },
    ],
  },
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.urgency || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(urgency, value)
  },
}

const jobTitle = 'jobTitle'
const jobTitleField: FormField = {
  candidateVisible: true,
  label: 'What job title is the team thinking for this role?',
  name: jobTitle,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.jobTitle || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(jobTitle, value)
  },
  placeholder: 'Enter the job title',
}

const externalReference = 'externalReference'
const externalReferenceField: FormField = {
  label: 'Reference number (optional)',
  name: externalReference,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().max(500, 'Reference number must be 500 characters or fewer'),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.externalReference || ''
  }),
  onUpdate: (value: string) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(externalReference, value)
  },
  placeholder: 'ATS requisition ID, spreadsheet reference, or other internal identifier',
  helpText: 'Use this to match the role to your ATS, spreadsheet, or another internal system. Yogen stores the value but does not connect to or access that system.',
  componentProps: {
    maxLength: 500,
  },
}

const alternativeJobTitles = 'alternativeJobTitles'
const alternativeJobTitlesField: FormField = {
  label: 'Are there other job titles that the team would consider using for this role?',
  name: alternativeJobTitles,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.alternativeJobTitles || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(alternativeJobTitles, value)
  },
  placeholder: 'Enter alternative titles for this role/Example: Developer/Engineer',
}

const keyOwnershipAreas = 'keyOwnershipAreas'
const keyOwnershipAreasField: FormField = {
  candidateVisible: true,
  label: 'What are the key ownership areas for this role?',
  name: keyOwnershipAreas,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.keyOwnershipAreas || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(keyOwnershipAreas, value)
  },
  placeholder: 'Enter key responsibilities here',
  helpText: "Let's list the 3-5 ownership responsibilities that will define their daily work. Verbs are your friends here! These are not the requirements or nice-to-haves.",
}

const projectsInitiatives = 'projectsInitiatives'
const projectsInitiativesField: FormField = {
  candidateVisible: true,
  label: 'What interesting projects/initiatives will be involved in this role?',
  name: projectsInitiatives,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.projectsInitiatives || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(projectsInitiatives, value)
  },
  placeholder: 'Enter projects and/or initiatives here',
  helpText: 'This is a major selling point! Every role has its crown jewel project. Highlighting the most exciting, challenging, or innovative work will attract top-tier talent who want to be challenged.',
}

const first3Months = 'first3Months'
const first3MonthsField: FormField = {
  candidateVisible: true,
  label: 'What will this person be working on in their first 3 months?',
  name: first3Months,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.first3Months || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(first3Months, value)
  },
  placeholder: 'Onboarding, learning our systems, and supporting the team with...',
}

const success12Months = 'success12Months'
const success12MonthsField: FormField = {
  candidateVisible: true,
  label: 'What would success look like after 12 months in this role?',
  name: success12Months,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.success12Months || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(success12Months, value)
  },
  placeholder: 'Managing key projects independently, contributing to team goals...',
}

const roleDetailsSection: FormSection = {
  id: 'roleDetailsSection',
  label: 'Role Details',
  description: 'Start with the role name, optional internal reference, and the reason the team is hiring.',
  fields: [
    jobTitleField,
    externalReferenceField,
    roleTypeField,
    urgencyField,
    businessNeedField,
  ],
}

const defineResponsibilitiesSection: FormSection = {
  id: 'defineResponsibilitiesSection',
  label: 'Define the Responsibilities',
  description: 'What would this person be spending their time on day-to-day, and who would they be partnering with across the team to get things done?',
  fields: [
    alternativeJobTitlesField,
    keyOwnershipAreasField,
    projectsInitiativesField,
    first3MonthsField,
    success12MonthsField,
  ],
}

const responsibilityCheckerSection: FormSection = {
  id: 'responsibilityCheckerSection',
  label: '',
  fields: [],
  displayComponents: [
    {
      name: 'CheckerPanel',
      label: 'Responsibility Checker',
      mode: 'responsibilities',
    },
  ],
}

const requiredSkills = 'requiredSkills'
const requiredSkillsField: FormField = {
  candidateVisible: true,
  label: 'What are the required/non-negotiable technical and soft skills?',
  name: requiredSkills,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.requiredSkills || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(requiredSkills, value)
  },
  placeholder: 'Enter required skills here',
  description: '(Have examples of tasks rather than years of proficiency. The Role-Requirement Fit Checker will help with suggestions.)\n\n(Learn about reducing bias and improving diversity.)',
  externalLink: {
    text: 'Reducing Bias and Improving DEI',
    url: 'https://getyogen.com/resources/reducing-bias-and-improving-dei',
  },
  helpText: "These are the 'must-haves'. Keeping this list short and truly essential helps us focus on what really matters and avoids screening out potentially great, diverse candidates.",
}

const niceToHaveSkills = 'niceToHaveSkills'
const niceToHaveSkillsField: FormField = {
  candidateVisible: true,
  label: 'What are the nice-to-have/teachable technical and soft skills?',
  name: niceToHaveSkills,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.niceToHaveSkills || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(niceToHaveSkills, value)
  },
  placeholder: 'Enter nice-to-have skills here',
}

const experienceLevel = 'experienceLevel'
const experienceLevelField: FormField = {
  candidateVisible: true,
  label: 'What is the seniority or experience level required for this role?',
  name: experienceLevel,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.experienceLevel || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(experienceLevel, value)
  },
  placeholder: 'Example: Junior-level, Mid-level, Senior-level',
  description: "(Seniority isn't standardized, job titles can vary by industry and company size. Learn more.",
  externalLink: {
    text: 'Hiring Requirements',
    url: 'https://getyogen.com/resources/outcome-based-hiring-requirements',
  },
  helpText: "This sets expectations for autonomy, strategic input, and salary. Being clear but flexible widens the talent pool. Higher seniority means harder-to-find candidates and higher salaries, so don't default to senior unless necessary",
}

const educationalBackground = 'educationalBackground'
const educationalBackgroundField: FormField = {
  candidateVisible: true,
  label: 'Are there any specific certifications or degrees the team wants for this role?',
  name: educationalBackground,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.educationalBackground || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(educationalBackground, value)
  },
  helpText: "Indicate each of these as mandatory or preferred.",
  placeholder: 'Enter educational preferences here',
}

const previousEnvironment = 'previousEnvironment'
const previousEnvironmentField: FormField = {
  candidateVisible: true,
  label: 'Is there any desired previous environment or specific company experience that the team expects for this role?',
  name: previousEnvironment,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.previousEnvironment || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(previousEnvironment, value)
  },
  placeholder: 'Example: startups, large corporate, government',
  helpText: "Is this person coming from a massive corporation or a scrappy startup? This isn't about snobbery; it's about work-style fit. It helps find someone who will adapt and be happy in your unique environment.",
}

const languageRequirements = 'languageRequirements'
const languageRequirementsField: FormField = {
  candidateVisible: true,
  label: 'What are the language requirements and the proficiency levels?',
  name: languageRequirements,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.languageRequirements || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(languageRequirements, value)
  },
  placeholder: 'English: Proficient / English: B1',
  helpText: 'Essential for effective teamwork, especially in an international setting. Be specific about the required level (A2-Basic, B1-Functional, B2-Competent, C1-Fluent, C2-Native) to avoid communication mishaps.',
}

const teamSize = 'teamSize'
const teamSizeField: FormField = {
  candidateVisible: true,
  label: "What does the team's composition (size and structure) look like?",
  name: teamSize,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.teamSize || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(teamSize, value)
  },
  placeholder: 'Example: The team consists of a manager, 2 designers, and 2 eng...',
}

const teamMethodologies = 'teamMethodologies'
const teamMethodologiesField: FormField = {
  candidateVisible: true,
  label: 'What frameworks or methodologies does the team use?',
  name: teamMethodologies,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.teamMethodologies || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(teamMethodologies, value)
  },
  placeholder: 'Example: Agile, Scrum, Waterfall...',
  helpText: 'Sell the vibe! Is the team heads-down and focused, or loud and collaborative? How do they handle mistakes? This is all about finding a great personality fit.',
}

const reportsTo = 'reportsTo'
const reportsToField: FormField = {
  candidateVisible: true,
  label: 'Who will this role report to?',
  name: reportsTo,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.reportsTo || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(reportsTo, value)
  },
  placeholder: 'Enter name',
}

const reportsToTitle = 'reportsToTitle'
const reportsToTitleField: FormField = {
  candidateVisible: true,
  label: 'What is their job title?',
  name: reportsToTitle,
  subLabel: 'a',
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.reportsToTitle || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(reportsToTitle, value)
  },
  placeholder: 'Enter job title',
  indent: true,
}

const reportsToLinkedIn = 'reportsToLinkedIn'
const reportsToLinkedInField: FormField = {
  candidateVisible: true,
  label: 'Enter their LinkedIn URL',
  name: reportsToLinkedIn,
  subLabel: 'b',
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.reportsToLinkedIn || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(reportsToLinkedIn, value)
  },
  placeholder: 'https://',
  indent: true,
}

const teamCulture = 'teamCulture'
const teamCultureField: FormField = {
  candidateVisible: true,
  label: 'Briefly describe the team culture.',
  name: teamCulture,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.teamCulture || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(teamCulture, value)
  },
  placeholder: "We're a collaborative bunch! We practice 'You build it, you run it.'...",
  helpText: 'What is your company culture built on? Describe the core values such as; collaboration, partnership, and accountability and outline what the team expects from members to be a strong cultural fit.',
}

const workSchedule = 'workSchedule'
const workScheduleField: FormField = {
  candidateVisible: true,
  label: 'What is the typical work schedule and flexibility for this role?',
  name: workSchedule,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.workSchedule || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(workSchedule, value)
  },
  placeholder: '10:00 - 16:00 CST with a flexibility...',
}

const workArrangement = 'workArrangement'
const workArrangementField: FormField = {
  candidateVisible: true,
  label: 'What is the work model for this role?',
  name: workArrangement,
  componentType: FORM_FIELD_TYPES.radio,
  componentProps: {
    options: [
      { value: 'remote', label: 'Remote' },
      { value: 'hybrid', label: 'Hybrid' },
      { value: 'inOffice', label: 'In-office' },
    ],
  },
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.workArrangement || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(workArrangement, value)
  },
}

const officeLocation = 'officeLocation'
const officeLocationField: FormField = {
  label: 'Where is this role based? (City, State or Country)',
  name: officeLocation,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  placeholder: 'Dallas, TX  or  London, UK',
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.officeLocation || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(officeLocation, value)
  },
  checkShouldShow: () => {
    const positionStore = usePositionStore()
    const arrangement = positionStore.selected?.positionData?.workArrangement
    return arrangement === 'hybrid' || arrangement === 'inOffice'
  },
}

const preferredTimezones = 'preferredTimezones'
const preferredTimezonesField: FormField = {
  candidateVisible: true,
  label: 'What are the timezones this role needs to support?',
  name: preferredTimezones,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.preferredTimezones || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(preferredTimezones, value)
  },
  placeholder: 'Ideally looking for someone in a CET +/- 3 hours...',
  helpText: 'This is crucial for managing expectations about synchronous work and team meetings.',
  checkShouldShow: () => {
  const positionStore = usePositionStore()
  const arrangement = positionStore.selected?.positionData?.workArrangement
  return arrangement === 'hybrid' || arrangement === 'inOffice'
},
}

const preferredDays = 'preferredDays'
const preferredDaysField: FormField = {
  candidateVisible: true,
  label: 'What time and days are required to be in office?',
  name: preferredDays,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.preferredDays || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(preferredDays, value)
  },
  placeholder: 'Tuesday to Thursday from 10am to 4pm...',
  checkShouldShow: () => {
  const positionStore = usePositionStore()
  const arrangement = positionStore.selected?.positionData?.workArrangement
  return arrangement === 'hybrid'
},
}

const toolsAndResources = 'toolsAndResources'
const toolsAndResourcesField: FormField = {
  candidateVisible: true,
  label: 'What software and hardware will the candidate use in this role?',
  name: toolsAndResources,
  componentType: FORM_FIELD_TYPES.inputCustom,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.toolsAndResources || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(toolsAndResources, value)
  },
  placeholder: 'Example: Figma, Cursor, Microsoft, Mac',
  helpText: 'Great people need great tools. This ensures alignment and candidate preference with what tools are best practices.',
}

const candidateProfileSection: FormSection = {
  id: 'candidateProfileSection',
  label: 'Define candidate profile',
  description: "We'd love to understand the most important elements to you in this hire? Both the must-have qualifications and the nice-to-have traits that would make someone really excel in this position.",
  fields: [
    requiredSkillsField,
    niceToHaveSkillsField,
    experienceLevelField,
    educationalBackgroundField,
    previousEnvironmentField,
    languageRequirementsField,
  ],
}

const teamEnvironmentSection: FormSection = {
  id: 'teamEnvironmentSection',
  label: 'Team Environment',
  description: 'What kind of team/environment will the candidate join?',
  fields: [
    teamSizeField,
    teamMethodologiesField,
    reportsToField,
    reportsToTitleField,
    reportsToLinkedInField,
    teamCultureField,
    workScheduleField,
    workArrangementField,
    officeLocationField,
    preferredTimezonesField,
    preferredDaysField,
    toolsAndResourcesField,
  ],
}

const requirementsCheckerSection: FormSection = {
  id: 'requirementsCheckerSection',
  label: '',
  fields: [],
  displayComponents: [
    {
      name: 'CheckerPanel',
      label: 'Role-Requirement Fit Checker',
      mode: 'candidate',
    },
  ],
}

const companySellingPoint = 'companySellingPoint'
const companySellingPointField: FormField = {
  candidateVisible: true,
  label: 'What are the top reasons someone would be excited to join the team and the company?',
  name: companySellingPoint,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.companySellingPoint || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(companySellingPoint, value)
  },
  placeholder: 'Enter in what makes this role compelling here',
  helpText: "Candidates may want to see a future beyond this role. Clear growth paths show you're invested in their long-term career, not just filling a position.",
}

const growthPossibilities = 'growthPossibilities'
const growthPossibilitiesField: FormField = {
  candidateVisible: true,
  label: 'Describe the growth and advancement opportunities for this role, if any?',
  name: growthPossibilities,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.growthPossibilities || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(growthPossibilities, value)
  },
  placeholder: 'Enter high level examples here',
  helpText: "What is the most compelling or unique aspect of this role. Try and combine the best parts of your mission, your tech, and your culture.",
}

const companyGoalsContribution = 'companyGoalsContribution'
const companyGoalsContributionField: FormField = {
  candidateVisible: true,
  label: 'What customer or business impact will this role drive?',
  name: companyGoalsContribution,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.companyGoalsContribution || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(companyGoalsContribution, value)
  },
  placeholder: 'Enter the impact this person will have on the product here',
  helpText: 'Different people align differently. Connect candidates day-to-day tasks to a bigger, tangible impact on your customers, the company, or even the world.',
}

const professionalDevelopmentBudget = 'professionalDevelopmentBudget'
const professionalDevelopmentBudgetField: FormField = {
  candidateVisible: true,
  label: 'Is there budget for professional development opportunities (like support learning training, or certifications) available for this role?',
  name: professionalDevelopmentBudget,
  componentType: FORM_FIELD_TYPES.yesOrNo,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    const val = positionStore.selected?.positionData?.professionalDevelopmentBudget
    return (val === 'Yes' || val === 'No') ? val : undefined
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(professionalDevelopmentBudget, value)
  },
}

const valuePropSection: FormSection = {
  id: 'valuePropSection',
  label: 'Value Proposition',
  description: 'How will the company sell this role?',
  fields: [
    companySellingPointField,
    growthPossibilitiesField,
    companyGoalsContributionField,
    professionalDevelopmentBudgetField,
  ],
  variant: DynamicFormVariant.Grid,
  numbered: true,
}

const valuePropCheckerSection: FormSection = {
  id: 'valuePropCheckerSection',
  label: '',
  fields: [],
  displayComponents: [
    {
      name: 'CheckerPanel',
      label: 'Value Prop Checker',
      mode: 'selling',
    },
  ],
}

const salaryRange = 'salaryRange'
const salaryRangeField: FormField = {
  label: 'What is the salary range or upper salary limit for this position?',
  name: salaryRange,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.salaryRange || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(salaryRange, value)
  },
  placeholder: 'Example: 90,000 - 100,000 USD annually',
  description: '(This information will need to be added manually to the job description)\nCandidates and internal employees will not see this data',
  helpText: "Let's talk about the elephant in the room! Defining this range up front improves offer acceptance rates. It dramatically reduces time-wasted on interviews where there's a salary mismatch.",
}

const salaryMarketCheckWidgetField: FormField = {
  label: '',
  name: 'salaryMarketCheckWidget',
  componentType: FORM_FIELD_TYPES.aiSalaryWidget,
  rules: Yup.mixed().optional(),
  required: false,
  value: computed(() => null),
  onUpdate: () => {},
}

const bonusEquityAvailable = 'bonusEquityAvailable'
const bonusEquityAvailableField: FormField = {
  label: 'Is there bonus potential, equity, stock options?',
  name: bonusEquityAvailable,
  componentType: FORM_FIELD_TYPES.yesOrNo,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    const val = positionStore.selected?.positionData?.bonusEquityAvailable
    return (val === 'Yes' || val === 'No') ? val : undefined
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(bonusEquityAvailable, value)
  },
}

const eligibleForRelocationAllowance = 'eligibleForRelocationAllowance'
const eligibleForRelocationAllowanceField: FormField = {
  candidateVisible: true,
  label: 'Is this role eligible for relocation allowance?',
  name: eligibleForRelocationAllowance,
  componentType: FORM_FIELD_TYPES.yesOrNo,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    const val = positionStore.selected?.positionData?.eligibleForRelocationAllowance
    return (val === 'Yes' || val === 'No') ? val : undefined
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(eligibleForRelocationAllowance, value)
  },
}

const benefitsPackage = 'benefitsPackage'
const benefitsPackageField: FormField = {
  candidateVisible: true,
  label: 'What benefits package is offered?',
  name: benefitsPackage,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.benefitsPackage || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(benefitsPackage, value)
  },
  placeholder: 'Amount of vacation days, healthcare deductibles, etc.',
  helpText: "Being detailed here will improve offer acceptance rates. There are many differences between healthcare plans and every candidates situation is unique. This helps to ensure that the total compensation is what an employee needs.",
}

const roleSpecificPerks = 'roleSpecificPerks'
const roleSpecificPerksField: FormField = {
  candidateVisible: true,
  label: 'Are there any perks specific to this role?',
  name: roleSpecificPerks,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.roleSpecificPerks || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(roleSpecificPerks, value)
  },
  placeholder: 'Low-monetary items, In-office perks, holiday gifts, quarterly parties..',
}

const salaryBenefitsSection: FormSection = {
  id: 'salaryBenefitsSection',
  label: 'Salary and Benefits',
  description: 'What are the expectations with salary and benefits?',
  fields: [
    salaryRangeField,
    salaryMarketCheckWidgetField,
    bonusEquityAvailableField,
    eligibleForRelocationAllowanceField,
    benefitsPackageField,
    roleSpecificPerksField,
  ],
  variant: DynamicFormVariant.Grid,
}

const mainCommunicationChannel = 'mainCommunicationChannel'
const mainCommunicationChannelField: FormField = {
  label: 'How will the hiring team communicate with each other about this role?',
  name: mainCommunicationChannel,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.mainCommunicationChannel || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(mainCommunicationChannel, value)
  },
  placeholder: 'Example: Email, Phone, slack, weekly meeting',
}

const feedbackAgreements = 'feedbackAgreements'
const feedbackAgreementsField: FormField = {
  label: 'How is the team going to leave post-interview candidate feedback?',
  name: feedbackAgreements,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.feedbackAgreements || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(feedbackAgreements, value)
  },
  description: '(Check out this external feature for everyone on the team)',
  externalLink: {
    text: 'Interview Co-Pilot',
    url: 'https://getyogen.com/copilot',
  },
  placeholder: 'Use Interview Co-Pilot to input into the ATS within 12 hours...',
}

const teamTimeCommitment = 'teamTimeCommitment'
const teamTimeCommitmentField: FormField = {
  label: "Is the interview team prepared to meet the time commitment required by this role's urgency?",
  name: teamTimeCommitment,
  componentType: FORM_FIELD_TYPES.yesOrNo,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    const val = positionStore.selected?.positionData?.teamTimeCommitment
    return (val === 'Yes' || val === 'No') ? val : undefined
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(teamTimeCommitment, value)
  },
  helpText: 'Consider whether interviewers understand the expected time investment and can realistically prioritize interviews given the defined urgency level. Misalignment here is a common reason roles fail to fill on time.',
}

const interviewSchedulingRestrictions = 'interviewSchedulingRestrictions'
const interviewSchedulingRestrictionsField: FormField = {
  label: "Are there any times when an interview shouldn't be scheduled?",
  name: interviewSchedulingRestrictions,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.interviewSchedulingRestrictions || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(interviewSchedulingRestrictions, value)
  },
  placeholder: "Example: The team doesn't interview on Fridays or after lunchtime...",
}

const interviewCoordinator = 'interviewCoordinator'
const interviewCoordinatorField: FormField = {
  label: 'Who will be responsible for co-ordinating and scheduling interviews and calendars?',
  name: interviewCoordinator,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.interviewCoordinator || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(interviewCoordinator, value)
  },
  placeholder: 'Example: John Collins or Hiring Manager',
}

const roleClosingMethod = 'roleClosingMethod'
const roleClosingMethodField: FormField = {
  label: 'How will the role be closed?',
  name: roleClosingMethod,
  componentType: FORM_FIELD_TYPES.radioSelect,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.roleClosingMethod || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(roleClosingMethod, value)
  },
  description: '(Compare the benefits and tradeoffs of each hiring decision model.)',
  externalLink: {
    text: 'Hiring Decision Models',
    url: 'https://getyogen.com/resources/hiring-decision-models-comparison',
  },
  componentProps: {
    options: [
      { value: 'first_past_the_post', label: 'First Past the Post' },
      { value: 'cohort_comparison', label: 'Cohort Comparison' },
      { value: 'threshold_based_hybrid', label: 'Threshold-Based Hybrid' },
      { value: 'time_boxed', label: 'Time-Boxed' },
    ],
  },
}

const collaborationAgreementsSection: FormSection = {
  id: 'collaborationAgreementsSection',
  label: 'Collaboration Agreements',
  description: 'This page sets the teams expectations for collaborating on this role.',
  fields: [
    mainCommunicationChannelField,
    feedbackAgreementsField,
    teamTimeCommitmentField,
    interviewSchedulingRestrictionsField,
    interviewCoordinatorField,
    roleClosingMethodField,
  ],
  variant: DynamicFormVariant.Grid,
  numbered: true,
}

const selectedHiringProcess = 'selectedHiringProcess'
const selectedHiringProcessField: FormField = {
  label: 'Select a hiring process template',
  name: selectedHiringProcess,
  componentType: FORM_FIELD_TYPES.selectCustom,
  componentProps: {
    placeholder: 'Choose a hiring process',
    options: (() => {
      let storeUpdated = false
      return () => {
        const hiringProcessStore = useHiringProcessStore()
        if (!storeUpdated) {
          hiringProcessStore.updateHiringProcessList()
          storeUpdated = true
        }
        return hiringProcessStore.list?.map((process) => ({
          value: process.id,
          label: process.name,
        })) ?? []
      }
    })(),
    ctaText: 'Create New Process +',
    ctaClick: () => {
      const modal = useGlobalModal()
      modal.open({
        component: defineAsyncComponent(
          () => import('~/components/process/AddHiringProcessModal.vue'),
        ),
        onClose: () => {
          const hiringProcessStore = useHiringProcessStore()
          hiringProcessStore.updateHiringProcessList()
        },
      })
    },
  },
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.selectedHiringProcess || ''
  }),
  displayValue: computed(() => {
    const hiringProcessStore = useHiringProcessStore()
    const positionStore = usePositionStore()
    if (positionStore.selected?.positionData?.selectedHiringProcess) {
      const process = hiringProcessStore.list?.find(
        (p) => p.id === positionStore.selected?.positionData?.selectedHiringProcess,
      )
      return process?.name ?? ''
    }
    return ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(selectedHiringProcess, value)
  },
  helpText: 'Choose from your saved hiring process templates or create a new one.',
}

const hiringProcessSteps = 'hiringProcessSteps'
const hiringProcessStepsField: FormField = {
  label: 'What are the steps in our hiring process?',
  name: hiringProcessSteps,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string(),
  value: computed(() => {
    const positionStore = usePositionStore()
    const hiringProcessStore = useHiringProcessStore()
    const selectedHiringProcessId = positionStore.selected?.positionData?.selectedHiringProcess
    const hiringProcessSteps = positionStore.selected?.positionData?.hiringProcessSteps
    if (selectedHiringProcessId) {
      const selectedProcess = hiringProcessStore.list?.find(
        (p) => p.id === selectedHiringProcessId,
      )
      if (selectedProcess?.processStages) {
        return selectedProcess.processStages
          .map((stage, stageIndex) => {
            const stageSteps = stage.processStageSteps
              .map((step) => `${step.type} (${step.duration}) - ${step.interviewLocation} - ${step.interviewerCount} interviewer(s)`)
              .join('\n  ')
            return `Stage ${stageIndex + 1}:\n  ${stageSteps}`
          })
          .join('\n\n')
      }
    }
    return hiringProcessSteps || ''
  }),
  onUpdate: () => {},
  disabled: true,
  helpText: 'Describe each step of the hiring process briefly.',
}

const hiringTimeline = 'hiringTimeline'
const hiringTimelineField: FormField = {
  label: 'What is our expected timeline for filling this position?',
  name: hiringTimeline,
  componentType: FORM_FIELD_TYPES.textarea,
  rules: Yup.string().required(),
  value: computed(() => {
    const positionStore = usePositionStore()
    return positionStore.selected?.positionData?.hiringTimeline || ''
  }),
  onUpdate: (value: any) => {
    const positionStore = usePositionStore()
    positionStore.updatePositionField(hiringTimeline, value)
  },
  helpText: 'What is your goal for when the role should be filled?',
}

const interviewProcessSection: FormSection = {
  id: 'interviewProcessSection',
  label: '',
  fields: [],
  displayComponents: [
    {
      name: 'InterviewPlanStep',
      label: 'Interview Process',
    },
  ],
}

const whoAreWeStep: FormStep = {
  id: 'whoAreWeStep',
  title: 'Who are we',
  sections: [companyInfoIntakeSection, companyTeamSection],
}

const definePositionStep: FormStep = {
  id: 'definePositionStep',
  title: 'Define Position',
  sections: [roleDetailsSection, defineResponsibilitiesSection, responsibilityCheckerSection],
}

const idealCandidateStep: FormStep = {
  id: 'idealCandidateStep',
  title: 'Ideal Candidate & Team Fit',
  sections: [candidateProfileSection, teamEnvironmentSection, requirementsCheckerSection],
}

const valuePropStep: FormStep = {
  id: 'valuePropStep',
  title: 'Value Proposition',
  sections: [valuePropSection, valuePropCheckerSection],
}

const salaryBenefitsStep: FormStep = {
  id: 'salaryBenefitsStep',
  title: 'Salary & Benefits',
  sections: [salaryBenefitsSection],
}

const collaborationAgreementsStep: FormStep = {
  id: 'collaborationAgreementsStep',
  title: 'Collaboration Agreements',
  sections: [collaborationAgreementsSection],
}

const interviewPlanStep: FormStep = {
  id: 'interviewPlanStep',
  title: 'Interview Plan',
  sections: [interviewProcessSection],
}

const intakeSummarySection: FormSection = {
  id: 'intakeSummarySection',
  label: '',
  fields: [],
  displayComponents: [
    {
      name: 'IntakeSummary',
      label: 'Intake Summary',
    },
  ],
}

const intakeSummaryStep: FormStep = {
  id: 'intakeSummaryStep',
  title: 'Intake Summary',
  sections: [intakeSummarySection],
}

export const positionFormDefinition: FormDefinition = {
  id: 'positionForm',
  name: 'Intake Meeting',
  steps: [
    whoAreWeStep,
    definePositionStep,
    idealCandidateStep,
    valuePropStep,
    salaryBenefitsStep,
    collaborationAgreementsStep,
    interviewPlanStep,
    intakeSummaryStep,
  ],
}
